import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Unlock } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

function formatTimeRemaining(expiresAt) {
  const now = new Date();
  const exp = new Date(expiresAt);
  const diffMs = exp - now;
  if (diffMs <= 0) return 'utgången';
  const h = Math.floor(diffMs / 3600000);
  const m = Math.floor((diffMs % 3600000) / 60000);
  if (h >= 1) return `${h}h ${m}m kvar`;
  return `${m}m kvar`;
}

const SuperAdminSupportBanner = () => {
  const { user, token } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [revoking, setRevoking] = useState(null);

  const fetchSessions = useCallback(async () => {
    if (!token || user?.role !== 'super_admin') {
      setSessions([]);
      return;
    }
    try {
      const resp = await axios.get(`${API_URL}/admin/support-sessions/my-active`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSessions(Array.isArray(resp.data) ? resp.data : []);
    } catch (err) {
      setSessions([]);
    }
  }, [token, user?.role]);

  useEffect(() => {
    fetchSessions();
    const id = setInterval(fetchSessions, 60_000);
    return () => clearInterval(id);
  }, [fetchSessions]);

  const handleRevoke = async (sessionId) => {
    if (!window.confirm('Avsluta supportläge för denna organisation?')) return;
    setRevoking(sessionId);
    try {
      await axios.delete(`${API_URL}/admin/support-sessions/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      await fetchSessions();
    } catch (err) {
      window.alert('Kunde inte avsluta sessionen.');
    } finally {
      setRevoking(null);
    }
  };

  if (user?.role !== 'super_admin' || sessions.length === 0) return null;

  return (
    <div
      data-testid="super-admin-support-banner"
      style={{
        background: '#fff7ed',
        borderBottom: '2px solid #fb923c',
        padding: '12px 24px',
        position: 'sticky',
        top: 0,
        zIndex: 30,
      }}
    >
      {sessions.map((s) => (
        <div
          key={s.id}
          className="flex flex-wrap items-center justify-between gap-3"
          style={{ color: '#9a3412' }}
        >
          <div className="flex items-center gap-2 text-sm">
            <Unlock size={16} />
            <span className="font-semibold">Supportläge aktivt</span>
            <span>—</span>
            <span className="font-semibold">{s.target_org_name || s.target_org_id}</span>
            <span>·</span>
            <span>{formatTimeRemaining(s.expires_at)}</span>
            <span>·</span>
            <span>{s.access_count} API-anrop</span>
          </div>
          <button
            onClick={() => handleRevoke(s.id)}
            disabled={revoking === s.id}
            className="text-sm font-semibold underline disabled:opacity-50"
            style={{ color: '#9a3412' }}
          >
            {revoking === s.id ? 'Avslutar...' : 'Avsluta'}
          </button>
        </div>
      ))}
    </div>
  );
};

export default SuperAdminSupportBanner;
